import type { ApiKeyDetail } from "@/api";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/i18n";
import { formatRelativeTimeID } from "@/lib/format";
import { ApiKeyActionsMenu } from "./api-key-actions-menu";
import { ApiKeyStatusBadge } from "./api-key-status-badge";

interface ApiKeysListProps {
  keys: ApiKeyDetail[];
  isLoading: boolean;
  onEdit: (key: ApiKeyDetail) => void;
  onDisable: (key: ApiKeyDetail) => void;
  onRevoke: (key: ApiKeyDetail) => void;
  onMutate: () => void;
}

export function ApiKeysList({
  keys,
  isLoading,
  onEdit,
  onDisable,
  onRevoke,
  onMutate,
}: ApiKeysListProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("apiKeys.table.name")}</TableHead>
            <TableHead>{t("apiKeys.table.key")}</TableHead>
            <TableHead>{t("apiKeys.table.status")}</TableHead>
            <TableHead className="hidden tablet:table-cell">
              {t("apiKeys.table.rateLimit")}
            </TableHead>
            <TableHead className="hidden tablet:table-cell">
              {t("apiKeys.table.lastUsed")}
            </TableHead>
            <TableHead className="w-12 text-right">
              {t("apiKeys.table.actions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && keys.length === 0 && (
            <>
              {[0, 1, 2].map((i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={6} className="py-4">
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            </>
          )}
          {!isLoading && keys.length === 0 && (
            <TableEmpty
              colSpan={6}
              message={t("apiKeys.table.emptyFiltered")}
            />
          )}
          {keys.map((apiKey) => (
            <TableRow key={apiKey.id}>
              <TableCell>
                <div className="font-medium text-ocean-900">{apiKey.name}</div>
                {apiKey.description && (
                  <div className="mt-0.5 text-xs text-neutral-500">
                    {apiKey.description}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <code className="rounded bg-neutral-50 px-2 py-1 font-mono text-xs text-neutral-600">
                  {apiKey.secret_key_display}
                </code>
              </TableCell>
              <TableCell>
                <ApiKeyStatusBadge status={apiKey.status} />
              </TableCell>
              <TableCell className="hidden text-neutral-600 tablet:table-cell">
                {t("apiKeys.table.ratePerMinute", {
                  count: apiKey.rate_limit_per_minute,
                })}
              </TableCell>
              <TableCell className="hidden text-neutral-600 tablet:table-cell">
                {apiKey.last_used_at
                  ? formatRelativeTimeID(apiKey.last_used_at)
                  : t("apiKeys.table.neverUsed")}
              </TableCell>
              <TableCell className="text-right">
                <ApiKeyActionsMenu
                  apiKey={apiKey}
                  onEdit={onEdit}
                  onDisable={onDisable}
                  onRevoke={onRevoke}
                  onEnableSuccess={onMutate}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
